import { useEntities } from "@app/store/entityStore";
import { DetailChip } from "@design-system/DataDisplay/DetailChip";
import { ProgressBar } from "@design-system/DataDisplay/ProgressBar/ProgressBar";
import { Skeleton } from "@design-system/DataDisplay/Skeleton";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import {
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Table,
    type ColumnDef,
    type Options,
} from "@design-system/Table";
import { Text } from "@design-system/Typography/Text";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useDebounce } from "@uidotdev/usehooks";
import chroma from "chroma-js";
import { useAtomValue, useSetAtom } from "jotai";
import {
    Fragment,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
    type FC,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { StringParam, useQueryParam } from "use-query-params";
import {
    selectedSubthemesAtom,
    selectedThemeIdAtom,
} from "../../components/CreateObjectiveModal/ctx";
import {
    isCreateObjectiveModalOpenAtom,
    selectedSubthemesAtomFamily,
    selectedThemesAtomFamily,
    selectedUsersAtomFamily,
} from "../../ctx";
import {
    useGroupedObjectivesWithDetails,
    useSubthemeProgress,
    useThemes,
} from "../../data";
import { keys, RESOURCE_NAME } from "../../i18n";
import { type Theme } from "../../types";
import {
    isGuidanceActiveAtom,
    selectedFrameworkAtom,
} from "../DittoGuidance/ctx";
import { useColumns } from "./columns";
import { EmptySearchState } from "./EmptySearchState";
import {
    type Objective,
    type ObjectiveGroupBySubtheme,
    type TableProps,
} from "./types";

export const GroupedTable: FC<TableProps> = ({
    entityId,
    onSelectionChange,
    ref,
}) => {
    const { org_uname } = useParams();

    const { entities } = useEntities();
    const { themes } = useThemes();
    const { subthemeProgressMap } = useSubthemeProgress(entityId);

    // Check if Ditto guidance is active and framework is EcoVadis
    const isGuidanceActive = useAtomValue(isGuidanceActiveAtom);
    const selectedFramework = useAtomValue(selectedFrameworkAtom);
    const shouldShowProgress =
        isGuidanceActive && selectedFramework === "ecovadis";

    const columns = useColumns();
    const selectedThemesIds = useAtomValue(selectedThemesAtomFamily(org_uname));
    const themeIdsFilter = useMemo(() => {
        return selectedThemesIds.filter((themeId) => themeId !== null);
    }, [selectedThemesIds]);

    const selectedSubthemesIds = useAtomValue(
        selectedSubthemesAtomFamily(org_uname),
    );
    const subthemeIdsFilter = useMemo(() => {
        return selectedSubthemesIds.filter((subthemeId) => subthemeId !== null);
    }, [selectedSubthemesIds]);

    const selectedUserIds = useAtomValue(selectedUsersAtomFamily(org_uname));

    const [urlSearchString] = useQueryParam("search", StringParam);
    const debouncedSearchString = useDebounce(urlSearchString, 300);

    const ownerIdsFilter = useMemo(() => {
        return selectedUserIds.filter((userId) => userId !== null);
    }, [selectedUserIds]);

    const { data: groupedObjectives, isPending } =
        useGroupedObjectivesWithDetails(
            {
                entityId,
                themeIds: themeIdsFilter,
                subthemeIds: subthemeIdsFilter,
                ownerIds: ownerIdsFilter,
                searchStr: debouncedSearchString,
            },
            { skip: entities?.length != 0 && !entityId },
        );

    const [rowSelection, setRowSelection] = useState({});
    useImperativeHandle(ref, () => ({
        unset: () => setRowSelection({}),
    }));

    const tableOptions = {
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onRowSelectionChange: setRowSelection,
        getPaginationRowModel: getPaginationRowModel(),
        getRowId: (row: Objective) => row.id,
        state: {
            rowSelection,
        },
        autoResetPageIndex: false,
    };

    const themesById = useMemo(() => {
        return themes.reduce(
            (acc, theme) => {
                if (!theme.id) return acc;
                if (acc[theme.id]) return acc;

                acc[theme.id] = theme;
                return acc;
            },
            {} as Record<string, Theme>,
        );
    }, [themes]);

    useEffect(() => {
        const selectedObjectives = Object.keys(rowSelection)
            .filter((k) => rowSelection[k as keyof typeof rowSelection])
            .filter((elt) => elt);
        onSelectionChange(selectedObjectives);
    }, [rowSelection]);

    return (
        <FlexCol pb="4" gap="6">
            {!isPending && groupedObjectives?.length === 0 && (
                <EmptySearchState />
            )}
            {isPending
                ? Array.from({ length: 5 }).map((_, index) => (
                      <Fragment key={index}>
                          <SectionSkeleton />
                          {index !== 4 && <hr className="border-border" />}
                      </Fragment>
                  ))
                : groupedObjectives?.map((section, index) => (
                      <Fragment key={index}>
                          <Section
                              key={section.subthemeId ?? section.themeId}
                              section={section}
                              tableOptions={tableOptions}
                              columns={columns}
                              isPending={isPending}
                              theme={themesById[section?.themeId ?? ""]}
                              subthemeProgressMap={subthemeProgressMap}
                              shouldShowProgress={shouldShowProgress}
                          />
                          {index !== groupedObjectives.length - 1 && (
                              <hr className="border-border" />
                          )}
                      </Fragment>
                  ))}
        </FlexCol>
    );
};

const Section: FC<{
    columns: ColumnDef<Objective>[];
    isPending: boolean;
    section: ObjectiveGroupBySubtheme;
    tableOptions: Options<Objective>;
    theme?: Theme;
    subthemeProgressMap: Map<
        string,
        { validatedObjectives: number; totalObjectives: number }
    >;
    shouldShowProgress: boolean;
}> = ({
    columns,
    isPending,
    section: { subthemeId, objectives, themeId },
    tableOptions,
    theme,
    subthemeProgressMap,
    shouldShowProgress,
}) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const setIsCreateObjectiveModalOpen = useSetAtom(
        isCreateObjectiveModalOpenAtom,
    );
    const setSelectedThemeId = useSetAtom(selectedThemeIdAtom);
    const setSelectedSubthemeId = useSetAtom(selectedSubthemesAtom);

    const name = useMemo(() => {
        if (!subthemeId) {
            return t(keys.theme_with_no_subthemes, {
                theme: theme?.name,
            });
        }
        return theme?.subthemes.find((subtheme) => subtheme.id === subthemeId)
            ?.name;
    }, [subthemeId, theme]);

    const subthemeProgress = subthemeId
        ? subthemeProgressMap.get(subthemeId)
        : null;

    const handleCreateObjectiveClick = () => {
        if (themeId) {
            setSelectedThemeId(themeId);
        }
        if (subthemeId) {
            setSelectedSubthemeId([subthemeId]);
        }
        setIsCreateObjectiveModalOpen(true);
    };

    return (
        <Collapsible.Root defaultOpen className="flex flex-col gap-3">
            <Collapsible.Trigger className="transition-all group">
                <FlexCol gap="2" w="full">
                    {/* Main header row */}
                    <FlexRow
                        gap="2"
                        alignItems="center"
                        minH="7"
                        justifyContent="between"
                        w="full"
                    >
                        <FlexRow gap="2" alignItems="center">
                            <Icon
                                className="text-primary transition-transform group-data-[state=closed]:-rotate-90"
                                name="caretDown"
                                size="xs"
                            />
                            <Icon
                                name={
                                    subthemeId ? "tagEmptyCircle" : "tagCircle"
                                }
                                color={theme?.color}
                            />
                            <Text color="primary" variant="body-lg-bold">
                                {name ?? t(keys.custom_theme)}
                            </Text>
                        </FlexRow>
                        <CreateObjectiveButton
                            onButtonClick={handleCreateObjectiveClick}
                        />
                    </FlexRow>

                    {/* Progress indicator row - only show when guidance is active */}
                    {shouldShowProgress && subthemeProgress && (
                        <FlexRow
                            gap="2"
                            alignItems="center"
                            w="full"
                            py="2"
                            ml="6"
                        >
                            <DetailChip>
                                <DetailChip.Icon name="logo" />
                            </DetailChip>
                            <ProgressBar
                                segments={[
                                    {
                                        color: chroma("#03A365"),
                                        value: subthemeProgress.validatedObjectives,
                                        label: t(keys.validated_objectives),
                                        labelValue:
                                            subthemeProgress.validatedObjectives.toString(),
                                    },
                                ]}
                                total={subthemeProgress.totalObjectives}
                                className="w-[60px]"
                                hideOnZeroTotal={false}
                            />
                            <Text variant="body-sm" color="secondary">
                                {t(keys.subtheme_progress, {
                                    validated:
                                        subthemeProgress.validatedObjectives,
                                    total: subthemeProgress.totalObjectives,
                                })}
                            </Text>
                        </FlexRow>
                    )}
                </FlexCol>
            </Collapsible.Trigger>
            <Collapsible.Content className="overflow-hidden transition-all duration-300 ease-out data-[state=closed]:max-h-0 data-[state=open]:max-h-[2000px] ml-6">
                {objectives.length > 0 ? (
                    <Table
                        columns={columns}
                        data={objectives}
                        options={tableOptions}
                        loading={isPending}
                        variant="grid"
                        hidePagination
                    />
                ) : (
                    <Text variant="body-sm-bold" color="secondary">
                        {t(keys.no_objectives_found_subtitle)}
                    </Text>
                )}
            </Collapsible.Content>
        </Collapsible.Root>
    );
};

const CreateObjectiveButton: FC<{
    onButtonClick: () => void;
}> = ({ onButtonClick }) => {
    const { t } = useTranslation(RESOURCE_NAME);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onButtonClick();
    };

    return (
        <Button size="sm" onClick={handleClick}>
            <Button.Icon name="plus" size="xs" />
            {t(keys.create_objective_short)}
        </Button>
    );
};

const SectionSkeleton: FC = () => {
    return (
        <FlexRow gap="2" alignItems="center" minH="7" my="3">
            <Icon className="text-tertiary" name="caretDown" size="xs" />
            <Box size="5" display="grid" className="place-items-center">
                <Skeleton className="h-3 w-3 rounded-full" />
            </Box>
            <Skeleton className="h-4 w-[250px]" />
        </FlexRow>
    );
};
